// OrgDetail — one tenant: its members, its load, its audit tail, and the two
// mutations that exist for an org (rename, deactivate).
//
// There is no delete. Domain rows reference orgs.id, and an org that has ever
// run a job is part of the audit trail; deactivation is the reversible form of
// the same intent and is what the backend implements.

import React from 'react';
import { api, assetUrl, errorText } from '../api';
import { createdToasts } from '../activationEmail';
import { useAsync } from '../useAsync';
import { navigate } from '../router';
import { ago, stamp, total } from '../format';
import { useToast } from '../ui/Toast';
import Sparkline from '../ui/Sparkline';
import UserSheet from '../ui/UserSheet';
import {
  Button, Card, ConfirmSheet, Empty, ErrorState, Field, Icon, KV, Note,
  PageHead, Pill, RefreshButton, Row, Sheet, Skeleton,
} from '../ui/primitives';

const ROLES = ['operator', 'approver', 'admin'];

function ProvisionSheet({ org, onClose, onDone }) {
  const toast = useToast();
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [role, setRole] = React.useState('operator');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const created = await api.provisionUser(org.id, { email: email.trim(), role, display_name: name.trim() });
      // V2-AC-02: the confirmation, then what happened to the activation email.
      createdToasts(created).forEach((t) => toast(t.text, t.tone));
      onDone();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title="Provision user" subtitle={org.name} onClose={onClose}
           footer={(
             <>
               <Button onClick={onClose} disabled={busy}>Cancel</Button>
               <Button variant="primary" loading={busy} disabled={!email.includes('@')}
                       onClick={submit}>Provision</Button>
             </>
           )}>
      <div className="stack">
        <Field label="Email"
               hint="Must be the Google account they will sign in with. Emails are unique across all of Mono.">
          <input className="input" value={email} type="email" autoFocus
                 autoCapitalize="none" autoCorrect="off" spellCheck={false}
                 inputMode="email"
                 onChange={(e) => setEmail(e.target.value)}
                 placeholder="name@company.com" />
        </Field>
        <Field label="Display name (optional)"
               hint="Left blank, it is filled in from Google on first sign-in.">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Role">
          <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        {err && <div className="note danger">{errorText(err)}</div>}
        <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          Provisioning creates the account but binds no Google identity — that
          happens the first time they sign in with this email. Where email is set
          up on this server, they are sent an activation email saying how.
        </div>
      </div>
    </Sheet>
  );
}

function RenameSheet({ org, onClose, onDone }) {
  const toast = useToast();
  const [name, setName] = React.useState(org.name);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  return (
    <Sheet title="Rename organisation" onClose={onClose}
           footer={(
             <>
               <Button onClick={onClose} disabled={busy}>Cancel</Button>
               <Button variant="primary" loading={busy}
                       disabled={!name.trim() || name.trim() === org.name}
                       onClick={async () => {
                         setBusy(true); setErr(null);
                         try {
                           await api.patchOrg(org.id, { name: name.trim() });
                           toast.ok('Renamed.');
                           onDone();
                         } catch (e) { setErr(e); } finally { setBusy(false); }
                       }}>Save</Button>
             </>
           )}>
      <div className="stack">
        <Field label="Name">
          <input className="input" value={name} autoFocus
                 onChange={(e) => setName(e.target.value)} />
        </Field>
        {err && <div className="note danger">{errorText(err)}</div>}
      </div>
    </Sheet>
  );
}

/**
 * The tenant's mark, shown in the main tool beside Mono's own wordmark — it
 * replaces the "Carton Imposition" strapline, and only when one is uploaded.
 *
 * The preview is drawn on the same paper the tool's header uses, at the same
 * height, because a logo that looks right on white and disappears on cream is
 * the failure this control exists to prevent.
 */
function LogoCard({ org, onChanged }) {
  const toast = useToast();
  const fileRef = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const url = assetUrl(org.logo_url);

  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      await api.uploadOrgLogo(org.id, file);
      toast.ok('Logo updated — it appears in the tool on the next load.');
      onChanged();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';   // re-pick the same file
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.deleteOrgLogo(org.id);
      toast.ok('Logo removed — the tool falls back to its own wordmark.');
      onChanged();
    } catch (e) {
      toast.error(errorText(e));
    } finally { setBusy(false); }
  };

  return (
    <Card title="Logo">
      <div className="stack">
        <div className="row" style={{
          gap: 12, padding: '12px 14px', borderRadius: 10,
          background: 'var(--surface)', border: '0.5px solid var(--rule)',
        }}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden>
            <rect x="1.6" y="1.6" width="18.8" height="18.8" rx="3"
                  stroke="var(--ink)" strokeWidth="1.2" />
            <rect x="6" y="6" width="10" height="10" rx="1.5" fill="var(--accent)" />
          </svg>
          <span style={{ fontFamily: 'var(--display)', fontSize: 20 }}>Mono</span>
          <span style={{
            borderLeft: '0.5px solid var(--rule)', paddingLeft: 12,
            display: 'flex', alignItems: 'center', minWidth: 0,
          }}>
            {url ? (
              <img src={url} alt={`${org.name} logo`}
                   style={{ height: 22, maxWidth: 180, objectFit: 'contain' }} />
            ) : (
              <span className="mono dim" style={{
                fontSize: 10, textTransform: 'uppercase', letterSpacing: 1,
              }}>Carton Imposition</span>
            )}
          </span>
        </div>
        <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
          {url
            ? 'This is how the header reads for everyone in this org.'
            : 'No logo yet — the tool shows its own strapline. Upload one to replace it.'}
          {' '}PNG, JPEG or WebP, under 512 KB. SVG is not accepted.
        </div>
        <input ref={fileRef} type="file" hidden
               accept="image/png,image/jpeg,image/webp"
               onChange={(e) => pick(e.target.files?.[0])} />
        <div className="btn-row">
          <Button small loading={busy} onClick={() => fileRef.current?.click()}>
            {url ? 'Replace' : 'Upload logo'}
          </Button>
          {url && (
            <Button small variant="danger" disabled={busy} onClick={remove}>
              Remove
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function OrgDetail({ orgId }) {
  const toast = useToast();
  const { data, error, loading, at, reload } = useAsync(() => api.org(orgId, { windowD: 30 }), [orgId]);
  const act = useAsync(() => api.activity(14, orgId), [orgId]);
  const [sheet, setSheet] = React.useState(null);      // 'provision' | 'rename'
  const [confirm, setConfirm] = React.useState(false);
  const [openUser, setOpenUser] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const toggleActive = async () => {
    setBusy(true);
    try {
      const r = await api.patchOrg(orgId, { is_active: !data.is_active });
      toast.ok(data.is_active
        ? `Deactivated — ${r.sessions_revoked} session(s) revoked.`
        : 'Organisation reactivated.');
      setConfirm(false);
      reload();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button className="btn ghost small" onClick={() => navigate('/orgs')}
              style={{ marginBottom: 6, paddingLeft: 0 }}>
        <Icon name="back" size={16} />All orgs
      </button>

      {error && <ErrorState error={error} text={errorText(error)} onRetry={reload} />}
      {!data && loading && <Skeleton rows={5} />}

      {data && (
        <>
          <PageHead
            title={data.name}
            sub={<span className="row" style={{ gap: 7 }}>
              <Pill tone={data.is_active ? 'ok' : 'danger'}>
                {data.is_active ? 'active' : 'deactivated'}
              </Pill>
              <span className="dim">created {data.created_at ? stamp(data.created_at) : '—'}</span>
            </span>}
            action={<RefreshButton onClick={reload} loading={loading} at={at} />} />

          {!data.is_active && (
            <Note tone="danger" title="Deactivated">
              Nobody in this organisation can sign in, and every session was
              revoked when it was switched off.
            </Note>
          )}

          <section className="section" style={{ marginTop: 14 }}>
            <div className="btn-row">
              <Button small onClick={() => setSheet('provision')}>
                <Icon name="plus" size={15} />Provision user
              </Button>
              <Button small onClick={() => setSheet('rename')}>Rename</Button>
              <Button small variant={data.is_active ? 'danger' : 'primary'}
                      disabled={busy}
                      onClick={() => (data.is_active ? setConfirm(true) : toggleActive())}>
                {data.is_active ? 'Deactivate' : 'Reactivate'}
              </Button>
              <Button small onClick={() => navigate(`/monitor?org=${orgId}`)}>
                Jobs & events
              </Button>
            </div>
          </section>

          <section className="section">
            <LogoCard org={data} onChanged={reload} />
          </section>

          <section className="section">
            <Card title={`Members · ${data.users.length}`} bodyless>
              <div className="list">
                {data.users.map((u) => (
                  <Row key={u.id} onClick={() => setOpenUser(u.id)}>
                    <div className="col" style={{ flex: 1 }}>
                      <div className="row" style={{ gap: 6 }}>
                        <span className="truncate">{u.display_name || u.email}</span>
                        {!u.is_active && <Pill tone="danger">off</Pill>}
                        {u.is_superuser && <Pill tone="accent">su</Pill>}
                      </div>
                      <div className="dim mono truncate" style={{ fontSize: 11 }}>
                        {u.email}
                      </div>
                    </div>
                    <Pill>{u.role}</Pill>
                  </Row>
                ))}
                {data.users.length === 0 && (
                  <Empty title="No members">Provision the first user above.</Empty>
                )}
              </div>
            </Card>
          </section>

          <section className="section">
            <Card title="Jobs · 30 days">
              {total(data.jobs.by_status) === 0 ? (
                <div className="dim" style={{ fontSize: 12.5 }}>No jobs in the window.</div>
              ) : (
                <div className="stack">
                  <div className="row row-wrap" style={{ gap: 6 }}>
                    {Object.entries(data.jobs.by_status).map(([k, v]) => (
                      <Pill key={k} tone={k === 'failed' ? 'danger' : k === 'succeeded' ? 'ok' : ''}>
                        {k} {v}
                      </Pill>
                    ))}
                  </div>
                  <KV items={[
                    ['By kind', Object.entries(data.jobs.by_kind)
                      .map(([k, v]) => `${k} ${v}`).join(' · ')],
                    ['Approvals', Object.entries(data.approvals || {})
                      .map(([k, v]) => `${k} ${v}`).join(' · ') || 'none'],
                    ['Roles', Object.entries(data.roles || {})
                      .map(([k, v]) => `${k} ${v}`).join(' · ')],
                  ]} />
                </div>
              )}
            </Card>
          </section>

          {act.data && (
            <section className="section">
              <Card title="Activity · 14 days">
                <Sparkline buckets={act.data.buckets} valueKey="jobs"
                           alertKey="jobs_failed" label="jobs per day" />
              </Card>
            </section>
          )}

          <section className="section">
            <Card title="Recent events" bodyless>
              <div className="list">
                {data.recent_events.map((e) => (
                  <div key={e.id} className="list-row">
                    <div className="col" style={{ flex: 1 }}>
                      <span className="mono truncate" style={{ fontSize: 12 }}>{e.event_type}</span>
                      <span className="dim" style={{ fontSize: 11 }}>
                        {e.actor_email || 'system'} · {ago(e.ts)}
                      </span>
                    </div>
                  </div>
                ))}
                {data.recent_events.length === 0 && (
                  <Empty title="Nothing yet">This org has recorded no events.</Empty>
                )}
              </div>
            </Card>
          </section>
        </>
      )}

      {sheet === 'provision' && (
        <ProvisionSheet org={data} onClose={() => setSheet(null)}
                        onDone={() => { setSheet(null); reload(); }} />
      )}
      {sheet === 'rename' && (
        <RenameSheet org={data} onClose={() => setSheet(null)}
                     onDone={() => { setSheet(null); reload(); }} />
      )}
      {confirm && (
        <ConfirmSheet
          title="Deactivate this organisation?"
          body={`Every one of ${data.name}'s ${data.users.length} users is signed out immediately and none can sign in again until it is reactivated. Jobs and history are kept.`}
          confirmLabel="Deactivate" danger confirmWord={data.name} busy={busy}
          onConfirm={toggleActive} onClose={() => setConfirm(false)} />
      )}
      {openUser && (
        <UserSheet userId={openUser} onClose={() => setOpenUser(null)} onChanged={reload} />
      )}
    </>
  );
}
